<template>
  <div class="dialog-overlay" @click.self="handleClose">
    <div class="dialog-container">
      <div class="dialog-header">
        <h3>{{ isEdit ? '시책 수정' : '시책 등록' }}</h3>
        <button class="btn-close" @click="handleClose">
          <i class="ph ph-x"></i>
        </button>
      </div>

      <div class="dialog-body">
        <!-- ① 기본 정보 -->
        <div class="form-section">
          <!-- 보험사 선택 -->
          <div class="form-group">
            <label class="required">보험사</label>
            <div class="company-select-wrapper">
              <div 
                class="company-select" 
                :class="{ open: companyDropdownOpen, selected: form.company_idx }"
                @click="companyDropdownOpen = !companyDropdownOpen"
              >
                <div v-if="selectedCompany" class="selected-company">
                  <img 
                    v-if="selectedCompany.short_img" 
                    :src="selectedCompany.short_img" 
                    :alt="selectedCompany.name"
                    class="company-logo"
                  />
                  <span>{{ selectedCompany.name }}</span>
                </div>
                <span v-else class="placeholder">보험사 선택</span>
                <i class="ph ph-caret-down arrow"></i>
              </div>
              
              <div v-if="companyDropdownOpen" class="company-dropdown">
                <template v-for="(group, type) in groupedCompanies" :key="type">
                  <div class="company-group-label">{{ convertTypeToName(type) }}</div>
                  <div 
                    v-for="company in group" 
                    :key="company.idx"
                    class="company-option"
                    :class="{ active: form.company_idx === company.idx }"
                    @click="selectCompany(company)"
                  >
                    <img 
                      v-if="company.short_img" 
                      :src="company.short_img" 
                      :alt="company.name"
                      class="company-logo"
                    />
                    <div v-else class="company-logo-placeholder">
                      {{ company.name.charAt(0) }}
                    </div>
                    <span>{{ company.name }}</span>
                    <i v-if="form.company_idx === company.idx" class="ph ph-check"></i>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="required">시책명</label>
            <input 
              v-model="form.title" 
              type="text" 
              class="form-input"
              placeholder="예: 삼성화재 1월 시책"
            />
          </div>

          <div class="form-group">
            <label>설명</label>
            <textarea 
              v-model="form.description" 
              class="form-textarea"
              rows="2"
              placeholder="시책에 대한 전체 설명 (선택)"
            ></textarea>
          </div>

          <!-- 시책 포스터 -->
          <div class="form-group">
            <label>시책 포스터 <span class="label-sub">전체 주차 내용이 담긴 이미지</span></label>
            <div class="poster-upload">
              <div v-if="form.image || previewImage" class="poster-preview">
                <img :src="previewImage || form.image" alt="" />
                <div class="poster-actions">
                  <button type="button" @click="triggerPosterInput">
                    <i class="ph ph-camera"></i>
                  </button>
                  <button type="button" @click="removePoster">
                    <i class="ph ph-trash"></i>
                  </button>
                </div>
              </div>
              <div v-else class="poster-placeholder" @click="triggerPosterInput">
                <i class="ph ph-image"></i>
                <span>포스터 업로드</span>
              </div>
              <input 
                ref="posterInput"
                type="file" 
                accept="image/*" 
                @change="handlePosterChange($event)"
                style="display: none;"
              />
            </div>
            <p v-if="uploading === 'poster'" class="upload-status">
              <i class="ph ph-spinner spinning"></i> 업로드 중...
            </p>
          </div>


        </div>

        <!-- ② 구간별 시책 (인라인 편집) -->
        <div class="periods-section">
          <div class="periods-header">
            <h4>
              <i class="ph ph-calendar-blank"></i>
              시책 구간
            </h4>
            <button type="button" class="btn-add" @click="addPeriod">
              <i class="ph ph-plus"></i>
              구간 추가
            </button>
          </div>

          <!-- 구간이 없을 때 -->
          <div v-if="form.weeks.length === 0" class="empty-periods">
            <p>등록된 구간이 없습니다</p>
            <button type="button" @click="addPeriod">
              <i class="ph ph-plus"></i>
              첫 구간 추가
            </button>
            
            <!-- 단일 시책 모드 (구간 없이 직접 입력) -->
            <div class="single-mode-divider">
              <span>또는 단일 시책으로 등록</span>
            </div>
            
            <div class="single-mode">
              <div class="form-row">
                <div class="form-group">
                  <label class="required">시작일</label>
                  <input v-model="form.start_date" type="date" class="form-input" />
                </div>
                <div class="form-group">
                  <label class="required">종료일</label>
                  <input v-model="form.expire_date" type="date" class="form-input" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label class="required">시책금액</label>
                  <div class="input-unit">
                    <input v-model.number="form.amount" type="number" class="form-input" placeholder="0" />
                    <span>%</span>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label class="required">목표</label>
                <textarea v-model="form.goal" class="form-textarea" rows="2" placeholder="목표 입력"></textarea>
              </div>
            </div>
          </div>

          <!-- 구간 목록 (인라인 카드) -->
          <div v-else class="period-list">
            <div 
              v-for="(period, index) in form.weeks" 
              :key="index"
              class="period-card"
            >
              <!-- Row 1: 라벨, 날짜, 삭제 -->
              <div class="period-row-main">
                <input 
                  v-model="period.label"
                  type="text"
                  class="input-label"
                  :placeholder="`${index + 1}구간`"
                />
                <div class="date-group">
                  <input v-model="period.start_date" type="date" class="input-date" />
                  <span class="date-sep">~</span>
                  <input v-model="period.end_date" type="date" class="input-date" />
                </div>
                <button 
                  type="button" 
                  class="btn-remove"
                  @click="removePeriod(index)"
                  title="구간 삭제"
                >
                  <i class="ph ph-x"></i>
                </button>
              </div>

              <!-- Row 2: 금액, 목표 -->
              <div class="period-row-detail">
                <div class="input-amount-wrap">
                  <input 
                    v-model.number="period.amount" 
                    type="number" 
                    class="input-amount"
                    placeholder="0"
                  />
                  <span class="amount-unit">%</span>
                </div>
                <input 
                  v-model="period.goal"
                  type="text"
                  class="input-goal"
                  placeholder="목표 (예: 10만원 이상)"
                />
              </div>

              <!-- Row 3: 이미지 (다중) -->
              <div class="period-row-images">
                <div 
                  v-for="(img, imgIdx) in getPeriodImages(period)" 
                  :key="imgIdx"
                  class="period-image-thumb"
                >
                  <img :src="img" alt="" />
                  <button type="button" @click="removePeriodImage(index, imgIdx)">
                    <i class="ph ph-x"></i>
                  </button>
                </div>
                <button 
                  type="button" 
                  class="btn-add-image"
                  @click="triggerPeriodFileInput(index)"
                >
                  <i class="ph ph-image"></i>
                  <span>{{ getPeriodImages(period).length > 0 ? '+' : '이미지' }}</span>
                </button>
                <input 
                  :ref="el => periodInputRefs[index] = el"
                  type="file" 
                  accept="image/*" 
                  multiple
                  @change="handlePeriodFileChange($event, index)"
                  style="display: none;"
                />
                <p v-if="uploading === `period-${index}`" class="upload-status-inline">
                  <i class="ph ph-spinner spinning"></i>
                </p>
              </div>
            </div>

            <!-- 구간 추가 버튼 (리스트 아래) -->
            <button type="button" class="btn-add-period-bottom" @click="addPeriod">
              <i class="ph ph-plus"></i>
              구간 추가
            </button>
          </div>
        </div>

        <!-- ③ 요약 (구간이 있을 때) -->
        <div v-if="form.weeks.length > 0" class="summary-bar">
          <div class="summary-item">
            <span class="sum-label">구간</span>
            <span class="sum-value">{{ form.weeks.length }}개</span>
          </div>
          <div class="summary-item">
            <span class="sum-label">기간</span>
            <span class="sum-value">{{ totalDateRange || '-' }}</span>
          </div>
          <div class="summary-item highlight">
            <span class="sum-label">최대 시책</span>
            <span class="sum-value">{{ formatNumber(maxAmount) }}%</span>
          </div>
        </div>
      </div>

      <div class="dialog-footer">
        <button class="btn btn-secondary" @click="handleClose">취소</button>
        <button 
          class="btn btn-primary" 
          @click="handleSubmit" 
          :disabled="!isValid || loading || !!uploading"
        >
          <i v-if="loading" class="ph ph-spinner spinning"></i>
          <span v-else>{{ isEdit ? '수정' : '등록' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, inject, defineProps, defineEmits } from 'vue'

const props = defineProps({
  editData: { type: Object, default: null },
  companies: { type: Array, default: () => [] }
})

const emit = defineEmits(['close', 'submit'])

const member_info = inject('member_info')

const loading = ref(false)
const uploading = ref(null)
const companyDropdownOpen = ref(false)
const posterInput = ref(null)
const previewImage = ref(null)
const periodInputRefs = ref({})

const form = ref({
  company_idx: '',
  title: '',
  description: '',
  goal: '',
  amount: 0,
  start_date: '',
  expire_date: '',
  image: '',
  extra_images: '',
  weeks: []
})

const isEdit = computed(() => !!props.editData)

// ── 보험사 ──
function convertTypeToName(type) {
  if (type === 'fire') return '화재보험'
  if (type === 'life') return '생명보험'
  return '기타'
}

const groupedCompanies = computed(() => {
  const sorted = [...props.companies].sort((a, b) => a.sort_no - b.sort_no)
  return sorted.reduce((acc, company) => {
    const type = company.type || '기타'
    if (!acc[type]) acc[type] = []
    acc[type].push(company)
    return acc
  }, {})
})

const selectedCompany = computed(() => {
  return props.companies.find(c => c.idx === form.value.company_idx)
})

function selectCompany(company) {
  form.value.company_idx = company.idx
  companyDropdownOpen.value = false
}

// ── 구간 관리 ──
function addPeriod() {
  const last = form.value.weeks[form.value.weeks.length - 1]
  let startDate = ''
  let endDate = ''
  
  if (last?.end_date) {
    const lastEnd = new Date(last.end_date)
    lastEnd.setDate(lastEnd.getDate() + 1)
    startDate = lastEnd.toISOString().slice(0, 10)
    const end = new Date(lastEnd)
    end.setDate(end.getDate() + 6)
    endDate = end.toISOString().slice(0, 10)
  } else {
    const today = new Date()
    startDate = today.toISOString().slice(0, 10)
    const end = new Date(today)
    end.setDate(end.getDate() + 6)
    endDate = end.toISOString().slice(0, 10)
  }
  
  form.value.weeks.push({
    label: '',
    week: form.value.weeks.length + 1,
    amount: 0,
    goal: '',
    start_date: startDate,
    end_date: endDate,
    image: '',
    images: [],
    extra_images: []
  })
}

function removePeriod(index) {
  if (!confirm('이 구간을 삭제하시겠습니까?')) return
  form.value.weeks.splice(index, 1)
}

// ── 유효성 ──
const isValid = computed(() => {
  const hasBasicInfo = form.value.company_idx && form.value.title.trim()
  
  if (form.value.weeks.length > 0) {
    const hasValidPeriods = form.value.weeks.every(w => 
      w.start_date && w.end_date
    )
    return hasBasicInfo && hasValidPeriods
  } else {
    return hasBasicInfo && 
      form.value.goal?.trim() &&
      form.value.start_date &&
      form.value.expire_date
  }
})

// ── 요약 계산 ──
const maxAmount = computed(() => {
  if (form.value.weeks.length === 0) return form.value.amount || 0
  return Math.max(...form.value.weeks.map(w => w.amount || 0))
})

const totalDateRange = computed(() => {
  if (form.value.weeks.length === 0) return ''
  const dates = form.value.weeks
    .filter(w => w.start_date && w.end_date)
    .flatMap(w => [new Date(w.start_date), new Date(w.end_date)])
  if (dates.length === 0) return ''
  const min = new Date(Math.min(...dates))
  const max = new Date(Math.max(...dates))
  return `${formatDateShort(min)} ~ ${formatDateShort(max)}`
})

// ── 이미지 업로드 ──
function triggerPosterInput() {
  posterInput.value?.click()
}

async function handlePosterChange(e) {
  const file = e.target.files?.[0]
  if (!file) return
  if (file.size > 5 * 1024 * 1024) {
    alert('파일 크기는 5MB 이하만 가능합니다.')
    return
  }
  previewImage.value = URL.createObjectURL(file)
  uploading.value = 'poster'
  const formData = new FormData()
  formData.append('idx', props.editData?.idx || '')
  formData.append('table', 'insu_policies')
  formData.append('usage', 'image')
  formData.append('user', member_info.mb_id)
  formData.append('file', file)
  try {
    const response = await fetch('/api/upload/image', {
      method: 'POST', body: formData, mode: 'cors', credentials: 'include'
    })
    const data = await response.json()
    if (data.url) {
      form.value.image = data.url.startsWith('/') ? window.location.origin + data.url : data.url
    } else {
      alert('이미지 업로드 실패')
      previewImage.value = null
    }
  } catch (err) {
    console.error('업로드 오류:', err)
    alert('업로드 중 오류 발생')
    previewImage.value = null
  } finally {
    uploading.value = null
  }
  e.target.value = ''
}

function removePoster() {
  form.value.image = ''
  previewImage.value = null
}

function triggerPeriodFileInput(index) {
  periodInputRefs.value[index]?.click()
}

// ── 구간 이미지 헬퍼 ──
function getPeriodImages(period) {
  // images 배열 우선, 없으면 image 단일 필드 폴백
  if (period.images && period.images.length > 0) return period.images
  if (period.image) return [period.image]
  return []
}

async function handlePeriodFileChange(e, periodIndex) {
  const files = Array.from(e.target.files || [])
  if (files.length === 0) return
  
  for (const file of files) {
    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB 이하만 가능합니다.')
      continue
    }
    uploading.value = `period-${periodIndex}`
    const formData = new FormData()
    formData.append('idx', props.editData?.idx || '')
    formData.append('table', 'insu_policies')
    formData.append('usage', 'week_image')
    formData.append('user', member_info.mb_id)
    formData.append('file', file)
    try {
      const response = await fetch('/api/upload/image', {
        method: 'POST', body: formData, mode: 'cors', credentials: 'include'
      })
      const data = await response.json()
      if (data.url) {
        const url = data.url.startsWith('/') ? window.location.origin + data.url : data.url
        if (!form.value.weeks[periodIndex].images) {
          form.value.weeks[periodIndex].images = []
        }
        form.value.weeks[periodIndex].images.push(url)
        // 첫 이미지를 대표로
        if (!form.value.weeks[periodIndex].image) {
          form.value.weeks[periodIndex].image = url
        }
      } else {
        alert('이미지 업로드 실패')
      }
    } catch (err) {
      console.error('업로드 오류:', err)
      alert('업로드 중 오류 발생')
    }
  }
  uploading.value = null
  e.target.value = ''
}

function removePeriodImage(periodIndex, imgIndex) {
  const period = form.value.weeks[periodIndex]
  if (period.images && period.images.length > 0) {
    period.images.splice(imgIndex, 1)
    period.image = period.images[0] || ''
  } else {
    period.image = ''
  }
}

// ── 포맷 ──
function formatNumber(num) {
  return num?.toLocaleString('ko-KR') || '0'
}

function formatDateShort(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

// ── 외부 클릭 ──
function handleClickOutside(e) {
  if (!e.target.closest('.company-select-wrapper')) {
    companyDropdownOpen.value = false
  }
}

// ── 데이터 초기화 ──
watch(() => props.editData, (newData) => {
  if (newData) {
    let weeks = []
    if (newData.weeks) {
      try {
        weeks = typeof newData.weeks === 'string' ? JSON.parse(newData.weeks) : newData.weeks
      } catch { weeks = [] }
    }
    // 기존 데이터에 label/images가 없으면 생성
    weeks = weeks.map(w => ({
      ...w,
      label: w.label || (w.week ? `${w.week}주차` : ''),
      images: w.images || (w.image ? [w.image] : [])
    }))
    
    form.value = {
      company_idx: newData.company_idx,
      title: newData.title,
      description: newData.description || '',
      goal: newData.goal || '',
      amount: newData.amount || 0,
      start_date: newData.start_date || '',
      expire_date: newData.expire_date || '',
      image: newData.image || '',
      extra_images: newData.extra_images || '',
      weeks
    }
    previewImage.value = null
  } else {
    resetForm()
  }
}, { immediate: true })

onMounted(() => { document.addEventListener('click', handleClickOutside) })
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (previewImage.value) URL.revokeObjectURL(previewImage.value)
})

function resetForm() {
  form.value = {
    company_idx: '', title: '', description: '', goal: '',
    amount: 0, start_date: '', expire_date: '',
    image: '', extra_images: '', weeks: []
  }
  previewImage.value = null
}

function handleClose() { emit('close') }

async function handleSubmit() {
  if (!isValid.value || loading.value || uploading.value) return
  loading.value = true
  try {
    let submitData = { ...form.value }
    
    if (form.value.weeks.length > 0) {
      const dates = form.value.weeks
        .filter(w => w.start_date && w.end_date)
        .flatMap(w => [new Date(w.start_date), new Date(w.end_date)])
      if (dates.length > 0) {
        submitData.start_date = new Date(Math.min(...dates)).toISOString().slice(0, 10)
        submitData.expire_date = new Date(Math.max(...dates)).toISOString().slice(0, 10)
      }
      submitData.amount = maxAmount.value
      submitData.goal = form.value.weeks
        .map(w => `${w.label || w.week + '주차'}: ${w.goal || '-'}`)
        .join('\n')
    }
    
    emit('submit', {
      ...submitData,
      weeks: JSON.stringify(form.value.weeks),
      isEdit: isEdit.value,
      idx: props.editData?.idx
    })
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.spinning { animation: spin 1s linear infinite; }

.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-top);
  padding: 16px;
  box-sizing: border-box;
}

.dialog-container {
  background: var(--color-background);
  border-radius: var(--radius-lg);
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--color-surface-reverse);
}

/* ── 헤더 ── */
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid var(--color-surface-reverse);
}

.dialog-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: var(--color-surface-reverse);
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.btn-close:hover {
  background: var(--color-text-third);
  color: var(--color-text-primary);
}

/* ── 바디 ── */
.dialog-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ── 폼 공통 ── */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
}

.form-group label.required::after {
  content: ' *';
  color: var(--color-special-warning);
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-surface-reverse);
  border-radius: var(--radius-md);
  font-size: 14px;
  color: var(--color-text-primary);
  background: var(--color-background);
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-brand);
}

.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-surface-reverse);
  border-radius: var(--radius-md);
  font-size: 14px;
  color: var(--color-text-primary);
  background: var(--color-background);
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
}

.form-textarea:focus {
  outline: none;
  border-color: var(--color-brand);
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-row .form-group {
  flex: 1;
}

.input-unit {
  display: flex;
  align-items: center;
  gap: 6px;
}

.input-unit span {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
}

/* ── 보험사 선택 ── */
.company-select-wrapper {
  position: relative;
}

.company-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid var(--color-surface-reverse);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: border-color 0.15s;
}

.company-select:hover,
.company-select.open {
  border-color: var(--color-brand);
}

.selected-company {
  display: flex;
  align-items: center;
  gap: 8px;
}

.company-logo {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  object-fit: contain;
}

.placeholder {
  color: var(--color-text-third);
  font-size: 14px;
}

.arrow {
  font-size: 14px;
  color: var(--color-text-third);
  transition: transform 0.2s;
}

.company-select.open .arrow {
  transform: rotate(180deg);
}

.company-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: var(--color-background);
  border: 1px solid var(--color-surface-reverse);
  border-radius: var(--radius-md);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 100;
}

.company-group-label {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-third);
  text-transform: uppercase;
}

.company-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.1s;
}

.company-option:hover {
  background: var(--color-surface-light);
}

.company-option.active {
  background: rgba(255, 140, 0, 0.08);
  color: var(--color-brand);
}

.company-option .ph-check {
  margin-left: auto;
  color: var(--color-brand);
}

.company-logo-placeholder {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: var(--color-surface-reverse);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text-secondary);
}

/* ── 포스터 이미지 ── */
.label-sub {
  font-weight: 400;
  color: var(--color-text-third);
  font-size: 12px;
}

.poster-upload {
  display: flex;
}

.poster-preview {
  position: relative;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.poster-preview img {
  width: 100%;
  display: block;
}

.poster-actions {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.15s;
}

.poster-preview:hover .poster-actions {
  opacity: 1;
}

.poster-actions button {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #333;
  transition: transform 0.15s;
}

.poster-actions button:hover {
  transform: scale(1.1);
}

.poster-placeholder {
  width: 100%;
  height: 100px;
  border: 2px dashed var(--color-surface-reverse);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.15s;
  color: var(--color-text-third);
}

.poster-placeholder:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

.poster-placeholder i {
  font-size: 24px;
}

.poster-placeholder span {
  font-size: 12px;
}

.upload-status {
  font-size: 12px;
  color: var(--color-brand);
  margin-top: 4px;
}

/* ── 구간 섹션 ── */
.periods-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.periods-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.periods-header h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.periods-header h4 i {
  color: var(--color-brand);
}

.btn-add {
  padding: 6px 12px;
  border: 1px dashed var(--color-brand);
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-brand);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s;
}

.btn-add:hover {
  background: rgba(255, 140, 0, 0.06);
}

/* 빈 상태 */
.empty-periods {
  text-align: center;
  padding: 16px 0;
}

.empty-periods > p {
  margin: 0 0 12px;
  font-size: 14px;
  color: var(--color-text-third);
}

.empty-periods > button {
  padding: 8px 16px;
  border: 1px dashed var(--color-brand);
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-brand);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.single-mode-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0 16px;
  font-size: 12px;
  color: var(--color-text-third);
}

.single-mode-divider::before,
.single-mode-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--color-surface-reverse);
}

.single-mode {
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;
}

/* ── 구간 카드 (인라인) ── */
.period-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.period-card {
  border: 1px solid var(--color-surface-reverse);
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--color-surface-light);
  transition: border-color 0.15s;
}

.period-card:focus-within {
  border-color: var(--color-brand);
}

/* Row 1: 라벨 + 날짜 + 삭제 */
.period-row-main {
  display: flex;
  align-items: center;
  gap: 8px;
}

.input-label {
  width: 100px;
  min-width: 80px;
  padding: 7px 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  background: transparent;
  transition: all 0.15s;
  box-sizing: border-box;
}

.input-label:hover {
  background: var(--color-background);
}

.input-label:focus {
  outline: none;
  border-color: var(--color-brand);
  background: var(--color-background);
}

.input-label::placeholder {
  color: var(--color-text-third);
  font-weight: 500;
}

.date-group {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
}

.input-date {
  flex: 1;
  padding: 7px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  color: var(--color-text-primary);
  background: transparent;
  transition: all 0.15s;
  min-width: 0;
}

.input-date:hover {
  background: var(--color-background);
}

.input-date:focus {
  outline: none;
  border-color: var(--color-brand);
  background: var(--color-background);
}

.date-sep {
  font-size: 12px;
  color: var(--color-text-third);
  flex-shrink: 0;
}

.btn-remove {
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: var(--color-text-third);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
}

.btn-remove:hover {
  background: rgba(218, 47, 71, 0.08);
  color: var(--color-special-warning);
}

/* Row 2: 금액 + 목표 */
.period-row-detail {
  display: flex;
  align-items: center;
  gap: 8px;
}

.input-amount-wrap {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.input-amount {
  width: 60px;
  padding: 7px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-brand);
  background: transparent;
  text-align: right;
  transition: all 0.15s;
}

.input-amount:hover {
  background: var(--color-background);
}

.input-amount:focus {
  outline: none;
  border-color: var(--color-brand);
  background: var(--color-background);
}

.input-amount::placeholder {
  color: var(--color-text-third);
  font-weight: 400;
}

/* Chrome number input 화살표 숨김 */
.input-amount::-webkit-outer-spin-button,
.input-amount::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.amount-unit {
  font-size: 14px;
  font-weight: 700;
  color: var(--color-brand);
}

.input-goal {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  color: var(--color-text-primary);
  background: transparent;
  transition: all 0.15s;
  min-width: 0;
}

.input-goal:hover {
  background: var(--color-background);
}

.input-goal:focus {
  outline: none;
  border-color: var(--color-brand);
  background: var(--color-background);
}

.input-goal::placeholder {
  color: var(--color-text-third);
}

/* Row 3: 이미지 (다중) */
.period-row-images {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.period-image-thumb {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
}

.period-image-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.period-image-thumb button {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  color: #fff;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;
}

.period-image-thumb:hover button {
  opacity: 1;
}

.btn-add-image {
  padding: 6px 10px;
  border: 1px dashed var(--color-surface-reverse);
  background: transparent;
  border-radius: 6px;
  font-size: 12px;
  color: var(--color-text-third);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s;
}

.btn-add-image:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

.upload-status-inline {
  font-size: 14px;
  color: var(--color-brand);
  margin: 0;
}

/* 하단 구간 추가 */
.btn-add-period-bottom {
  width: 100%;
  padding: 10px;
  border: 1px dashed var(--color-surface-reverse);
  background: transparent;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-third);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.15s;
}

.btn-add-period-bottom:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
  background: rgba(255, 140, 0, 0.03);
}

/* ── 요약 ── */
.summary-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: var(--color-surface-light);
  border-radius: 10px;
  border: 1px solid var(--color-surface-reverse);
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.summary-item.highlight {
  margin-left: auto;
}

.sum-label {
  font-size: 12px;
  color: var(--color-text-third);
}

.sum-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.summary-item.highlight .sum-value {
  color: var(--color-brand);
  font-size: 16px;
}

/* ── 푸터 ── */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid var(--color-surface-reverse);
  background: var(--color-surface-light);
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--color-background);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-surface-reverse);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-surface-light);
}

.btn-primary {
  background: var(--color-brand);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.1);
}

/* 반응형 */
@media (max-width: 480px) {
  .dialog-body {
    padding: 16px;
  }
  
  .period-row-main {
    flex-wrap: wrap;
  }
  
  .input-label {
    width: 100%;
    min-width: unset;
  }
  
  .date-group {
    flex: unset;
    width: 100%;
  }
  
  .period-row-detail {
    flex-wrap: wrap;
  }
  
  .input-goal {
    flex: unset;
    width: 100%;
  }
  
  .summary-bar {
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .summary-item.highlight {
    margin-left: 0;
    width: 100%;
  }
}
</style>